import { useState } from "react";
import {
  useParams,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2,
  Eye,
  Kanban,
  FileText,
  Receipt,
  GitPullRequest,
  ArrowLeft,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useServiceOrders } from "@/providers/ServiceOrdersProvider";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getPackageDisplayName } from "@/domain/packageUtils";
import { OverviewSection } from "./OverviewSection";
import { ProgressSection } from "./ProgressSection";
import { ContractsSection } from "./ContractsSection";
import { BillingSection } from "./BillingSection";
import { ChangeRequestsSection } from "./ChangeRequestsSection";

function validateProjectName(name: string): string | null {
  if (!name || typeof name !== "string") {
    return "Project name is required";
  }
  const trimmed = name.trim();
  if (trimmed.length < 3) {
    return "Project name must be at least 3 characters";
  }
  if (trimmed.length > 100) {
    return "Project name must be less than 100 characters";
  }
  if (!/^[a-zA-Z0-9\s\-_&.]+$/.test(trimmed)) {
    return "Project name can only contain letters, numbers, spaces, hyphens, underscores, ampersands, and periods";
  }
  return null;
}

interface LocationState {
  openNewRequest?: boolean;
}

const navigationItems = [
  { id: "overview", label: "Overview", icon: Eye, path: "overview" },
  { id: "progress", label: "Progress", icon: Kanban, path: "progress" },
  { id: "contracts", label: "Contracts", icon: FileText, path: "contracts" },
  { id: "billing", label: "Billing", icon: Receipt, path: "billing" },
  {
    id: "changeRequests",
    label: "Change Requests",
    icon: GitPullRequest,
    path: "changeRequests",
  },
];

export function WorkOrderDetailPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const locationState = location.state as LocationState | null;
  const workOrderId = params.workOrderId;
  const section = params["*"]?.split("/").pop() || "overview";

  const { getServiceOrderById, isLoading, error, updateProjectName } =
    useServiceOrders();
  const serviceOrder = workOrderId
    ? getServiceOrderById(workOrderId)
    : undefined;

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleEditStart = () => {
    setEditedName(
      serviceOrder?.customProjectName || serviceOrder?.project.name || ""
    );
    setNameError(null);
    setIsEditingName(true);
  };

  const handleEditCancel = () => {
    setIsEditingName(false);
    setEditedName("");
    setNameError(null);
  };

  const handleEditSave = async () => {
    if (!serviceOrder) return;

    const validationError = validateProjectName(editedName);
    if (validationError) {
      setNameError(validationError);
      return;
    }

    setIsSaving(true);
    try {
      await updateProjectName(serviceOrder.id, editedName.trim());
      setIsEditingName(false);
      setEditedName("");
      toast({
        title: "Project name updated",
        description: "Your project name has been saved successfully.",
      });
    } catch (err) {
      toast({
        title: "Failed to update",
        description:
          err instanceof Error ? err.message : "Could not save project name",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleEditSave();
    } else if (e.key === "Escape") {
      handleEditCancel();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (!serviceOrder) {
    return <Navigate to="/service-orders" replace />;
  }

  const renderSection = () => {
    switch (section) {
      case "overview":
        return <OverviewSection serviceOrder={serviceOrder} />;
      case "progress":
        return <ProgressSection serviceOrder={serviceOrder} />;
      case "contracts":
        return <ContractsSection serviceOrder={serviceOrder} />;
      case "billing":
        return <BillingSection serviceOrder={serviceOrder} />;
      case "changeRequests":
        return (
          <ChangeRequestsSection
            serviceOrder={serviceOrder}
            openNewRequest={locationState?.openNewRequest}
          />
        );
      default:
        return <OverviewSection serviceOrder={serviceOrder} />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <div>
        <button
          onClick={() => navigate("/service-orders")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Service Orders
        </button>

        <div className="mb-4">
          {isEditingName ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={editedName}
                  onChange={(e) => {
                    setEditedName(e.target.value);
                    setNameError(null);
                  }}
                  onKeyDown={handleKeyDown}
                  className="text-2xl font-bold h-auto py-1 px-2 max-w-md"
                  autoFocus
                  disabled={isSaving}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleEditSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 text-emerald-500" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleEditCancel}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
              {nameError && (
                <p className="text-sm text-destructive">{nameError}</p>
              )}
            </div>
          ) : (
            <div className="group">
              <div className="flex items-center gap-2">
                <h1 className="font-display text-2xl font-bold">
                  {serviceOrder.customProjectName || serviceOrder.project.name}
                </h1>
                <button
                  onClick={handleEditStart}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
                  title="Edit project name"
                >
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground">
                {getPackageDisplayName(serviceOrder.packageId)}
              </p>
            </div>
          )}
        </div>

        <nav className="flex items-center gap-1 border-b border-border/50">
          {navigationItems.map((item) => {
            const isActive = section === item.path;
            return (
              <button
                key={item.id}
                onClick={() =>
                  navigate(`/service-orders/${workOrderId}/${item.path}`)
                }
                className={cn(
                  "flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {renderSection()}
    </motion.div>
  );
}
